/**
 * MapShell.tsx — Top-level page: TopBar + MapView + responsive ScorePanel
 *
 * Layout:
 *   <div flex-col h-screen>
 *   ├── TopBar          — h-12 header with logo + sign-out (SC-04)
 *   └── <div relative flex-1>
 *       ├── MapView (fills remaining height)
 *       │   ├── TractLayer        — choropleth null-rendering layer (MAP-01/MAP-04)
 *       │   ├── EVChargerLayer    — EV charger symbol layer (MAP-03, toggleable)
 *       │   ├── POILayerMgr       — POI marker layer (MAP-03, toggleable)
 *       │   ├── PinMarker         — draggable drop-a-pin (SCORE-05)
 *       │   ├── MapSearchBar      — address search overlay [absolute top-4 left-4]
 *       │   ├── EmptyState        — before first search [absolute top-20 center]
 *       │   ├── LayersControl     — layer toggles [absolute bottom-4 left-4] (SC-04)
 *       │   └── ScorePanel (≥md)  — compact card [absolute top-16 right-4 w-80] (SC-02)
 *       ├── InfoSidePanel (≥md)   — slide-out full details [fixed top-12 right-0] (T-007)
 *       └── Drawer (<md)          — vaul bottom sheet, modal=false, snap 18/85% (SC-02)
 *
 * State:
 *   bbox              — viewport bbox from MapView moveend/load
 *   activeTract       — tract from search or pin; drives ScorePanel
 *   activeLocation    — geocode lat/lng; drives EV/POI layers
 *   pinLocation       — current pin position (null = no pin)
 *   geocodeConfidence — 'exact' | 'approximate' | null (from MapSearchBar, resets on pin drop)
 *   panelOpen         — InfoSidePanel visible (desktop) / Drawer at full snap (mobile)
 *   showEVChargers    — toggle EV charger layer
 *   showPOI           — toggle POI layer
 *   siteScoreResult   — latest /api/score/site result (SCORE-03)
 *   siteScoreLoading  — true while fetchSiteScore is in flight
 *   siteScoreError    — error copy string on failure; null on success
 */
import { useState, useEffect, useRef, useMemo } from 'react'
import { X, ChevronLeft, ChevronUp, Layers } from 'lucide-react'
import { cn } from '@/lib/utils'
import { MapView } from '../components/map/MapView'
import { TractLayer, TRACT_TIERS, TRACT_TYPES } from '../components/map/TractLayer'
import { StateLayer } from '../components/map/StateLayer'
import { CountyLayer } from '../components/map/CountyLayer'
import { PmtilesVectorLayer } from '../components/map/PmtilesVectorLayer'
import { RoadsLayer } from '../components/map/RoadsLayer'
import { SiteVicinityLayer } from '../components/map/SiteVicinityLayer'
import { useSiteVicinity } from '../hooks/useSiteVicinity'
import { selectLocationClassification } from '../lib/locationClassification'
import type { LocationClassification } from '../lib/locationClassification'
import { TileLoadingIndicator } from '../components/map/TileLoadingIndicator'
import { tierForZoom, detailForZoom, type MapTier } from '../lib/mapTiers'
import { initialCameraBounds } from '../lib/coverageCamera'
import { stateLabel } from '../lib/stateNames'
import { EVChargerLayer } from '../components/map/EVChargerLayer'
import { IsochroneLayer } from '../components/map/IsochroneLayer'
import { IsochroneControl } from '../components/IsochroneControl'
import { POILayerMgr, POI_CATEGORY_NAMES, POI_MIN_ZOOM } from '../components/map/POILayerMgr'
import { PinMarker } from '../components/map/PinMarker'
import { MapSearchBar } from '../components/map/MapSearchBar'
import { ScorePanel } from '../components/ScorePanel'
import { TopTractsSidePanel } from '../components/map/TopTractsSidePanel'
import { TopTractsList } from '../components/map/TopTractsList'
import { TopTractsCta } from '../components/map/TopTractsCta'
import { InfoSidePanel } from '../components/InfoSidePanel'
// T-495: the same drill-down InfoSidePanel shows on desktop, rendered inside the mobile sheet.
import { ScoreDrillDown } from '../components/ScoreDrillDown'
import { TopBar } from '../components/TopBar'
import { EmptyState } from '../components/EmptyState'
import { Spinner } from '../components/ui/spinner'
import { LayersControl } from '../components/LayersControl'
import { ScoreLegend } from '../components/ScoreLegend'
import { EVLegend } from '../components/EVLegend'
import { RoadsLegend } from '../components/RoadsLegend'
import { Drawer, DrawerContent } from '../components/ui/drawer'
import { useMediaQuery } from '../hooks/useMediaQuery'
import { fetchSiteScore, authedFetch, fetchTractByGeoid, reverseGeocode } from '../lib/api'
import { hydrateSelectedTract } from '../lib/hydrateSelectedTract'
import { fetchAndApplySiteScore } from '../lib/applySiteScore'
import { resolveAndApplyAddress } from '../lib/applyReverseGeocode'
import { track, coarseGeoFromGeoid, round2 } from '../lib/analytics'
import { featurePropsToTract } from '../lib/tractModel'
import { shouldAutoEnablePoi } from '../lib/poiAutoEnable'
import { useAuth } from '../auth/useAuth'
import { SettingsDialog } from '../components/SettingsDialog'
import { LeaveAppDialog } from '../components/LeaveAppDialog'
import { AdvisorMount } from '../components/AdvisorMount'
import { AdvisorLauncher } from '../components/AdvisorLauncher'
import { useMapUrlState, type RestoredMapState } from '../hooks/useMapUrlState'
import { useCoverage } from '../hooks/useCoverage'
import { useTileAuth } from '../hooks/useTileAuth'
import type { CensusTract, SiteScoreResult, TractFeatureProps } from '../lib/tractModel'

interface ActiveLocation {
  lat: number
  lng: number
}

// Copywriting Contract (UI-SPEC §Copywriting)
const COPY_NO_COVERAGE = 'No coverage at this location. Try a nearby address.'
const COPY_SCORE_UNAVAILABLE = 'Score unavailable. Please try again.'

// T-222 r2 (P2): session-scoped keys for the one-time POI auto-enable and the permanent
// manual-off decision. sessionStorage → per-tab session; a reload keeps the decision.
const POI_AUTO_ENABLED_KEY = 'optiyield_poi_auto_enabled'
const POI_MANUAL_OFF_KEY = 'optiyield_poi_manual_off'

// Mobile bottom-sheet snap points, as fractions of the viewport height (SC-02).
// T-495 named them: the peek fraction is also how far the map chrome has to lift to stay clear
// of the sheet, and the two must not drift apart.
const SHEET_PEEK = 0.18
const SHEET_FULL = 0.85

interface MapShellProps {
  /**
   * T-649: open the admin PAGE — a top-level view switch owned by AuthedApp, which replaced the
   * old admin dialog. Optional so tests can mount MapShell bare; when absent the admin entry is
   * simply not offered (TopBar gates on `isAdmin && onOpenAdmin`).
   */
  onOpenAdmin?: () => void
}

export function MapShell({ onOpenAdmin }: MapShellProps = {}) {
  const [bbox, setBbox] = useState<[number, number, number, number] | null>(null)
  // T-010/T-098: zoom-driven level-of-detail. `zoom` picks the tract geometry detail; `tier`
  // (with hysteresis) picks which geography layer is visible — state at national zoom-out
  // (z<5), county at regional zoom (5≤z<9), tract when zoomed in (z≥9). INITIAL_ZOOM mirrors
  // MapView's national default camera zoom (CONUS z4, T-092 A1) so the STATE tier is active
  // from the first frame (the national default shows the state choropleth, not a blank map);
  // if they ever diverge the first frame self-corrects when MapView's `load` fires onZoomChange.
  const INITIAL_ZOOM = 4
  const [zoom, setZoom] = useState<number>(INITIAL_ZOOM)
  const [tier, setTier] = useState<MapTier>(() => tierForZoom(INITIAL_ZOOM))
  const [activeTract, setActiveTract] = useState<CensusTract | null>(null)
  // T-233: mirror activeTract into a ref so the async ranked-list geoid-hydration can check, at
  // apply time, whether the selection it started is still current — guarding the rapid-click and
  // superseded-by-map-click races without a stale-closure read of activeTract.
  const activeTractRef = useRef<CensusTract | null>(null)
  useEffect(() => {
    activeTractRef.current = activeTract
  }, [activeTract])
  const [activeLocation, setActiveLocation] = useState<ActiveLocation | null>(null)
  const [pinLocation, setPinLocation] = useState<{ lat: number; lng: number } | null>(null)
  // T-429: true while the address field is focused or still holds a query — see the EmptyState
  // render below for why.
  const [searchActive, setSearchActive] = useState(false)
  const [siteScoreResult, setSiteScoreResult] = useState<SiteScoreResult | null>(null)
  const [siteScoreLoading, setSiteScoreLoading] = useState(false)
  const [siteScoreError, setSiteScoreError] = useState<string | null>(null)
  // T-234: monotonic id for the latest in-flight /api/score/site request. Rapid pin-drops/searches
  // can resolve out of order; the apply-time guard in fetchAndSetSiteScore applies a response only
  // if its id is still current, so a stale earlier response never overwrites the latest score.
  const siteScoreReqIdRef = useRef(0)
  // T-072 item 1: the pin-drop → tract-resolve step was fully silent. Track it so a
  // small "Locating tract…" indicator shows while resolveTractForPin is in flight.
  const [resolvingTract, setResolvingTract] = useState(false)

  // Plan 02.1-06 / T-007: state for responsive panel + layer toggles
  const [geocodeConfidence, setGeocodeConfidence] = useState<'exact' | 'approximate' | null>(null)
  // The current site's address string, kept client-side for the panel display, the PDF export
  // header, and the ROI calculator context. Two provenances:
  //   • a SEARCH sets the typed address (exact; T-258 — never re-sent from here, the search POST
  //     already logged it to our Postgres);
  //   • T-478: a map CLICK reverse-geocodes the clicked point (approximate) — `addressApproximate`
  //     records which, so the UI can disclaim a map-derived address.
  // A ranked-list selection has no real address, so that path clears both.
  const [searchedAddress, setSearchedAddress] = useState<string | null>(null)
  // T-478: true when `searchedAddress` came from reverse-geocoding a map click (approximate).
  const [addressApproximate, setAddressApproximate] = useState(false)
  // T-478: monotonic id for the latest reverse-geocode lookup (same shape as siteScoreReqIdRef).
  // Rapid clicks — and a search/ranked-pick landing mid-lookup — can resolve out of order; the
  // apply-time guard in resolveAndApplyAddress stamps an address only if its id is still current,
  // so a stale click's address never overwrites the newer location's. Bumped by every path that
  // sets the address, which invalidates any reverse-geocode still in flight.
  const reverseGeoReqIdRef = useRef(0)
  const [panelOpen, setPanelOpen] = useState(false)
  /**
   * T-495: full metrics showing INSIDE the mobile sheet. `panelOpen` cannot carry this on its
   * own — on mobile it also means "sheet at full snap", which a swipe sets, so keying the
   * drill-down off it would swap the content on every swipe-up. This is set only by the
   * "View all metrics" button and cleared centrally by the effect below, so every existing
   * path that closes the detail (new selection, ranked list, swipe down, restore) clears it
   * too and a stale drill-down can never outlive its tract.
   */
  const [mobileDetail, setMobileDetail] = useState(false)
  /** T-495: the map chrome (legends + drive-time + layers) is collapsed by default on mobile. */
  const [chromeOpen, setChromeOpen] = useState(false)
  // T-495: one place to retire the mobile drill-down. Closing the detail is already spelled
  // `setPanelOpen(false)` in six places (new selection, ranked list, swipe down, hash restore,
  // …); deriving from it here means none of them can forget, and none of them has to know.
  useEffect(() => {
    if (!panelOpen) setMobileDetail(false)
  }, [panelOpen])
  // T-222/BEAD-0040 R2: the ranked "Top tracts in view" list is now a CTA-opened slide-out
  // (Ricardo's revision — no longer always docked). `rankOpen` drives it, reusing the
  // tract-detail (InfoSidePanel) idiom on desktop and the bottom drawer on mobile.
  const [rankOpen, setRankOpen] = useState(false)
  const [showTracts, setShowTracts] = useState(true)
  // T-501: AI Strategy Advisor slide-over open state (opened by the floating launcher).
  const [advisorOpen, setAdvisorOpen] = useState(false)
  const [showEVChargers, setShowEVChargers] = useState(true)
  // T-052: POI layer OFF by default (opt-in). POI data is only fetched around a searched
  // location, so defaulting the layer ON left a fresh load showing an enabled-but-empty
  // layer — read by users as "POIs not rendering". Off by default; the user opts in via
  // LayersControl, and POILayerMgr renders the fetched POIs once a search sets activeLocation.
  const [showPOI, setShowPOI] = useState(false)
  // T-222 r2 (P2): session-scoped flags backing the one-time POI auto-enable.
  // `poiAutoEnabledRef` caps it at once per session; `poiManualOffRef` makes a manual
  // toggle-off permanent (never auto-re-enable). Both seed from sessionStorage.
  const poiAutoEnabledRef = useRef<boolean>(
    typeof sessionStorage !== 'undefined' && sessionStorage.getItem(POI_AUTO_ENABLED_KEY) === '1',
  )
  const poiManualOffRef = useRef<boolean>(
    typeof sessionStorage !== 'undefined' && sessionStorage.getItem(POI_MANUAL_OFF_KEY) === '1',
  )
  // T-061: NHS Interstate + US Route overlay OFF by default (opt-in overlay). The layer
  // self-gates to zoom >= ROADS_MIN_ZOOM, so enabling it at national zoom does nothing until
  // the user zooms into regional range — keeping the low-zoom view to the choropleth story.
  const [showRoads, setShowRoads] = useState(false)
  // T-508: the T-504 site-vicinity micro-layer, OFF by default — an opt-in overlay like POI
  // (T-052) and Major roads (T-061), not a base layer like tracts/EV. Same reasoning T-052
  // recorded for POI: the readout only has data around a searched site, and only where blocks
  // are loaded, so defaulting it ON shows an enabled-but-empty layer that reads as broken.
  // T-504 shipped it rendered unconditionally with no way to turn it off; this is the toggle.
  const [showVicinity, setShowVicinity] = useState(false)
  // T-135: drive-time isochrone duration around the active pin. null = Off (default —
  // opt-in, so no GeoRoutes call fires until the user picks a duration). IsochroneLayer
  // consumes activeLocation (the same pin the EV/POI layers use) + this value.
  const [isochroneMinutes, setIsochroneMinutes] = useState<5 | 10 | 15 | null>(null)
  // T-012/W3: POI category filter — default all 12 enabled.
  const [enabledPOICategories, setEnabledPOICategories] = useState<string[]>(POI_CATEGORY_NAMES)
  // T-012/W4: tract tier + land-use filter — default all selected (no filtering).
  const [tractTierFilter, setTractTierFilter] = useState<string[]>([...TRACT_TIERS])
  const [tractTypeFilter, setTractTypeFilter] = useState<string[]>([...TRACT_TYPES])
  // T-092: the app's loaded-region coverage (GET /api/coverage). `extent` derives the
  // opening camera (A1); `states` resolves state FIPS → abbreviation below. On empty/error
  // it stays empty → the map opens on the national default.
  // T-279: the coverage banner this also fed is gone; the hook stays for the two uses above.
  const coverage = useCoverage()
  // T-308: the union `extent` is only a trustworthy camera when the coverage it spans is
  // contiguous — with HI (or, later, AK) loaded it centres on open Pacific. initialCameraBounds
  // returns null for that case, which is MapView's existing "open on the CONUS default"
  // contract. See lib/coverageCamera.ts for the rule and its trade-off.
  const initialBounds = useMemo(
    () => initialCameraBounds(coverage.states, coverage.extent),
    [coverage.states, coverage.extent],
  )
  // T-126: resolve a 2-digit state FIPS → abbreviation from the loaded coverage, so
  // analytics events carry a readable state (falls back to the FIPS when unknown).
  const fipsToAbbrev = (fips: string): string | undefined =>
    coverage.states.find((s) => s.state_fips === fips)?.state_abbrev

  // T-258: the state label for the PDF export header + filename, derived from the active tract's
  // GEOID (first 2 digits = state FIPS) via the loaded coverage. Null when no tract is resolved.
  const exportStateLabel = activeTract
    ? (fipsToAbbrev(activeTract.tractId.slice(0, 2)) ?? null)
    : null

  // T-126: emit map_zoom_tier_changed only on an actual tier boundary crossing (setTier
  // is called on every moveend; the ref-guarded effect fires once per committed change).
  const prevTierRef = useRef<MapTier>(tier)
  useEffect(() => {
    if (prevTierRef.current !== tier) {
      track('map_zoom_tier_changed', { from_tier: prevTierRef.current, to_tier: tier })
      prevTierRef.current = tier
    }
  }, [tier])

  // T-222 r2 (P2): auto-enable the POI layer ONCE, only when the user is both past
  // POI_MIN_ZOOM and has an activeLocation (so it renders data, not an empty layer — the T-052
  // guard). shouldAutoEnablePoi also blocks a second firing and any re-enable after a manual
  // toggle-off. On the toggle→true, showPOI changes and this effect re-runs but now returns
  // false (already on), so there is no loop.
  useEffect(() => {
    if (
      shouldAutoEnablePoi({
        zoom,
        minZoom: POI_MIN_ZOOM,
        hasLocation: activeLocation !== null,
        showPOI,
        alreadyAutoEnabled: poiAutoEnabledRef.current,
        manuallyDisabled: poiManualOffRef.current,
      })
    ) {
      poiAutoEnabledRef.current = true
      try {
        sessionStorage.setItem(POI_AUTO_ENABLED_KEY, '1')
      } catch {
        /* storage unavailable — the ref still caps it to once per component lifetime */
      }
      setShowPOI(true)
    }
  }, [zoom, activeLocation, showPOI])

  // T-102: signed-URL transport for the aggregate tiers. When tile-auth returns per-tier signed
  // pmtiles URLs, the state + county tiers render from the private CloudFront PMTiles pyramids;
  // when it's unavailable (503 — distribution not wired / key missing) `tiles` is null and the
  // tiers fall back to the authenticated bbox-GeoJSON layers. Transport per the T-102 topology memo.
  const { tiles: tileUrls } = useTileAuth()

  // T-116: graceful degradation for the pmtiles aggregate tiers. A signed URL can be valid at
  // /api/tile-auth (200) yet still 403 at CloudFront (expired / placeholder-key signature) or fail
  // on the network — MapLibre then renders the tier BLANK with no recovery (the ~1h48m degraded
  // window on 2026-07-06, where tile-auth 200'd but every tile fetch 403'd). When either pmtiles
  // source reports such an error we fall the aggregate tiers back to the bbox-GeoJSON layers, which
  // are self-sufficient. The flag resets whenever useTileAuth hands back a fresh signed-URL object
  // (its ~12 h refresh), so a transient failure recovers to the pmtiles path on the next refresh.
  const [pmtilesError, setPmtilesError] = useState(false)
  // T-275/F3: the tract bbox API caps a dense viewport and flags it. Holds the count
  // actually shown (from the payload, NOT a copy of the server cap), or null when not
  // capped. Nothing read that flag, so a capped viewport (NYC/Northeast at z9 with the
  // country loaded) rendered an incomplete map that looked complete. Surfaced as a pill.
  const [truncatedShown, setTruncatedShown] = useState<number | null>(null)
  useEffect(() => {
    setPmtilesError(false)
  }, [tileUrls])
  const usePmtiles = tileUrls !== null && !pmtilesError

  // Signed-in user's email (from the Cognito session via AuthContext) + Settings modal state.
  const { user, signOut } = useAuth()
  const userEmail = user?.email ?? ''
  const [settingsOpen, setSettingsOpen] = useState(false)

  // T-237: an imperative camera target for a Back/Forward restore (a new nonce = one jumpTo).
  const [commandedViewport, setCommandedViewport] = useState<{
    center: [number, number]
    zoom: number
    nonce: number
  } | null>(null)
  const restoreNonceRef = useRef(0)
  // Guards the async tract re-hydration during a restore: only the latest requested geoid applies,
  // so a slow fetch from an earlier Back/Forward can't overwrite a newer one.
  const lastRestoreGeoidRef = useRef<string | null>(null)

  // T-237: apply a decoded hash entry (boot deep-link or a Back/Forward pop) to app state —
  // pin/location + the selected tract (re-hydrated authoritatively by geoid via T-233's
  // fetchTractByGeoid). The viewport is handled separately (MapView initialViewport on boot,
  // commandedViewport on a pop), so this only owns the selection.
  function applyRestoredSelection(state: RestoredMapState) {
    setPanelOpen(false)
    setGeocodeConfidence(null)
    // The hash carries no address; a restored pin has no reverse-geocoded street address, so the
    // PDF export header falls back to GEOID (T-258) rather than showing a stale one.
    setSearchedAddress(null)
    setAddressApproximate(false)
    if (state.pin) {
      setPinLocation(state.pin)
      setActiveLocation(state.pin) // drives the EV/POI layers, same as a live selection
    } else {
      setPinLocation(null)
      setActiveLocation(null)
    }
    lastRestoreGeoidRef.current = state.tract
    if (state.tract) {
      void hydrateSelectedTract(state.tract, {
        fetchTract: fetchTractByGeoid,
        isStillSelected: (geoid) => lastRestoreGeoidRef.current === geoid,
        apply: setActiveTract,
      })
    } else {
      setActiveTract(null)
      setSiteScoreResult(null)
      setSiteScoreError(null)
      setSiteScoreLoading(false)
    }
  }

  // T-237: the URL hash is the SPA's restore/deep-link state (no router — Ricardo's ruling). This
  // hook owns browser History: it SUBSUMES the old T-071 back-navigation guard (still warns before a
  // Back that would exit the app — {showLeaveDialog, stay, leave} below) AND adds deep-linking:
  // syncViewport (throttled replaceState on pan), commitSelection (pushState so Back = prior
  // selection), and onRestore (Back/Forward re-applies the popped entry's viewport + tract).
  const urlState = useMapUrlState({
    active: !!user,
    onRestore: (state) => {
      restoreNonceRef.current += 1
      setCommandedViewport({
        center: [state.lng, state.lat],
        zoom: state.zoom,
        nonce: restoreNonceRef.current,
      })
      applyRestoredSelection(state)
    },
  })
  const { showLeaveDialog, stay, leave } = urlState
  // Deep-link opening camera (parsed once from the boot hash) — wins over the coverage fit.
  const initialViewport = urlState.initial
    ? {
        center: [urlState.initial.lng, urlState.initial.lat] as [number, number],
        zoom: urlState.initial.zoom,
      }
    : null

  // T-237 boot: re-hydrate the deep-linked selection once on mount (the camera is already seeded via
  // MapView's initialViewport). Viewport-only deep-links need no selection restore.
  useEffect(() => {
    if (urlState.initial && (urlState.initial.tract || urlState.initial.pin)) {
      applyRestoredSelection(urlState.initial)
    }
    // Boot-once: intentionally not re-run when initial (stable) or the setters change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // ── T-628: resolve the site's block / block-group classification ONCE ────────────────────────
  // The compact card and the drill-down both need it now, and useSiteVicinity's own note called
  // this: "if the two ever need to share one request, lift the fetch to MapShell and pass data to
  // both". One request, one answer — the card can never disagree with the panel about which block
  // the site sits in. Keyed on activeLocation, which is the point every readout describes.
  const { vicinity: siteVicinity } = useSiteVicinity(activeLocation)
  const siteClassification: LocationClassification | null = useMemo(
    () =>
      activeLocation && siteVicinity
        ? selectLocationClassification(activeLocation, siteVicinity)
        : null,
    [activeLocation, siteVicinity],
  )

  // SC-02: side panel ≥md, bottom Drawer <md
  const isMd = useMediaQuery('(min-width: 768px)')

  // T-081 F0: the site-score feedback (loading / no-coverage error / result) is scoped to the
  // searched LOCATION, not to a resolved tract. Surface the panel whenever there is such
  // feedback — even with no activeTract — so an out-of-coverage search shows the "No coverage"
  // copy instead of dead-ending silently.
  const hasScoreFeedback = siteScoreLoading || siteScoreError !== null || siteScoreResult !== null
  // T-495: the mobile sheet is showing (at peek or full) — the same condition that opens the
  // Drawer below, named once because the map chrome has to sit clear of it.
  const sheetShowing = rankOpen || activeTract !== null || hasScoreFeedback

  /**
   * Shared site-score fetch path — called by both handlePinMoved and handleSearchResult.
   * Maps 404 → no-coverage copy; other errors → unavailable copy.
   */
  function fetchAndSetSiteScore(lat: number, lng: number) {
    // T-234: claim the latest-request slot synchronously (in call order), then reset the panel to
    // the loading state. The response is applied only if this id is still current when it resolves,
    // so an out-of-order earlier response can't overwrite a newer selection's score.
    const reqId = ++siteScoreReqIdRef.current
    setSiteScoreLoading(true)
    setSiteScoreError(null)
    setSiteScoreResult(null)
    void fetchAndApplySiteScore(lat, lng, {
      fetchSiteScore,
      isCurrent: () => reqId === siteScoreReqIdRef.current,
      applyResult: setSiteScoreResult,
      applyError: setSiteScoreError,
      finish: () => setSiteScoreLoading(false),
      mapError: (err) => {
        const status = err instanceof Error ? err.message : ''
        return status.includes('404') ? COPY_NO_COVERAGE : COPY_SCORE_UNAVAILABLE
      },
    })
  }

  /**
   * Resolve the census tract at a free-form pin location.
   * Falls back gracefully — if the bbox call fails, activeTract stays as-is.
   */
  async function resolveTractForPin(lat: number, lng: number): Promise<CensusTract | null> {
    setResolvingTract(true)
    try {
      // T-153/BEAD-0060: resolve the CONTAINING tract (point-in-polygon), same as the search
      // path — not the old bbox → features[0], which returned an arbitrary neighbour so the
      // panel eyebrow/sub-indices disagreed with the tract the pin visually sits in.
      const params = new URLSearchParams({ lat: String(lat), lng: String(lng) })
      const res = await authedFetch(`/api/tracts/at?${params}`)
      if (!res.ok) return null
      const fc = (await res.json()) as { features?: { properties: unknown }[] }
      const top = fc.features?.[0]
      if (top) {
        const t = featurePropsToTract(top.properties as TractFeatureProps)
        setActiveTract(t)
        return t
      }
      // T-081 F1: the bbox call SUCCEEDED but the pin is genuinely out of coverage (zero
      // tracts). Clear the stale tract so the panel doesn't show a previous location's score
      // beside the "No coverage" banner. (A transient FAILURE — !res.ok / throw — still
      // leaves the prior tract in place, handled by the early return / catch below.)
      setActiveTract(null)
      return null
    } catch {
      // Non-fatal — panel stays in State A with existing tract (or hidden if none)
      return null
    } finally {
      setResolvingTract(false)
    }
  }

  /**
   * Called by PinMarker on dragend and map click (SCORE-05).
   * G-1: re-resolves tract on EVERY pin move (not just when activeTract is null).
   * Resets confidence to null — pin drops have no geocode (no address lookup).
   */
  function handlePinMoved(loc: { lat: number; lng: number }, method: 'click' | 'drag') {
    setPinLocation(loc)
    // T-115: drive activeLocation too, so a pin-drop populates the EV + POI layers the same
    // way a search does. Both EVChargerLayer and POILayerMgr key their fetch on activeLocation;
    // previously only handleSearchResult set it, so a fresh pin-drop showed a score but no
    // nearby-context markers. ActiveLocation is {lat,lng} only — a pin has no geocode, and POI
    // stays zoom-gated (POI_MIN_ZOOM) so this won't fire a heavy low-zoom query.
    setActiveLocation({ lat: loc.lat, lng: loc.lng })
    setGeocodeConfidence(null) // pin drops have no geocode confidence
    // T-478: recover an APPROXIMATE address for the clicked point (parity with search — a clicked
    // site should not be address-less). Clear the previous address synchronously (so a stale one
    // never lingers under the new pin), claim the latest reverse-geo slot, then fill it in
    // asynchronously. Best-effort: a miss/failure leaves the cleared state and the export/ROI fall
    // back to the GEOID, exactly as before — the parallel scoring flow is never blocked by it.
    setSearchedAddress(null)
    setAddressApproximate(false)
    const geoReqId = ++reverseGeoReqIdRef.current
    void resolveAndApplyAddress(loc.lat, loc.lng, {
      reverseGeocode,
      isCurrent: () => geoReqId === reverseGeoReqIdRef.current,
      apply: (address) => {
        setSearchedAddress(address)
        setAddressApproximate(true) // reverse-geocoded → approximate, disclaimed in the UI
      },
    })

    // G-1: re-resolve the tract on EVERY pin move so the panel eyebrow + component
    // indices match the scored location. T-126: fire pin_dropped once the tract resolves,
    // carrying the resolved geoid + coarse geo (the app's primary site-evaluation gesture).
    void resolveTractForPin(loc.lat, loc.lng).then((tract) => {
      const geo = coarseGeoFromGeoid(tract?.tractId, fipsToAbbrev)
      track('pin_dropped', {
        method,
        tract_geoid: tract?.tractId ?? null,
        county: geo.county,
        state: geo.state,
        center_lat: round2(loc.lat),
        center_lng: round2(loc.lng),
      })
      // T-237: commit the pin-drop as a discrete history entry once the tract geoid is resolved,
      // so Back returns to (and re-hydrates) this scored site. One pushState per pin-drop.
      urlState.commitSelection({ tract: tract?.tractId ?? null, pin: loc })
    })

    void fetchAndSetSiteScore(loc.lat, loc.lng)
  }

  /**
   * Called by MapSearchBar after a geocode resolves. Syncs pin to the searched location
   * (search→pin sync, UI-SPEC §4) and ALWAYS runs the shared site-score path.
   *
   * T-081 F0/F1: `tract` may be null (out-of-coverage search). We set activeTract
   * UNCONDITIONALLY — a null clears any stale tract from a prior in-coverage selection, so the
   * "No coverage" banner never shows beside a previous location's score/ID (F1). The shared
   * fetchAndSetSiteScore then maps the 404 to COPY_NO_COVERAGE, and hasScoreFeedback surfaces
   * the panel even with no tract (F0) — the search path now behaves exactly like map-click.
   */
  function handleSearchResult(
    tract: CensusTract | null,
    location: ActiveLocation,
    confidence: 'exact' | 'approximate',
    address: string,
  ) {
    setActiveTract(tract) // null clears the stale tract on an out-of-coverage search (F1)
    setActiveLocation(location)
    setPinLocation(location)
    setGeocodeConfidence(confidence)
    setSearchedAddress(address) // T-258: stamp this address on a subsequent PDF export
    setAddressApproximate(false) // T-478: a searched/typed address is exact-provenance — no disclaimer
    // T-478: invalidate any reverse-geocode still in flight from a prior click, so its late address
    // can't overwrite this search's exact address.
    reverseGeoReqIdRef.current++
    setPanelOpen(false) // close detail panel on new selection
    // T-126: address_searched carries COARSE geo only (county/state derived from the
    // resolved tract). The full address goes ONLY to our Postgres via MapSearchBar's
    // POST /api/events/search — never to the analytics vendor.
    const geo = coarseGeoFromGeoid(tract?.tractId, fipsToAbbrev)
    track('address_searched', {
      county: geo.county,
      state: geo.state,
      result_found: tract !== null,
    })
    void fetchAndSetSiteScore(location.lat, location.lng)
    // T-237: a search is a discrete selection → push a history entry so Back returns to the
    // previous one. The camera flyTo's moveend then replaceState-updates this entry's viewport.
    urlState.commitSelection({ tract: tract?.tractId ?? null, pin: location })
  }

  /**
   * T-126: open the full-details panel and record metrics_panel_opened. `source`
   * distinguishes the desktop side-panel button from the mobile drawer button.
   */
  function openFullDetails(source: 'desktop_panel' | 'mobile_drawer') {
    setRankOpen(false) // one right-edge surface at a time — the detail replaces the ranked list
    setPanelOpen(true)
    // T-495: desktop gets the metrics from InfoSidePanel; mobile has no side panel, so the
    // sheet has to render them itself. Without this the button only raised the snap point —
    // and once the sheet was already full (the only state it is reachable from) it did nothing.
    if (source === 'mobile_drawer') setMobileDetail(true)
    track('metrics_panel_opened', {
      context: activeTract ? 'tract' : 'location',
      source,
    })
  }

  /**
   * T-222 R2: open the ranked-list surface (CTA → slide-out). Closing the tract detail keeps
   * the "one right-edge surface at a time" idiom, and records the same panel-open engagement
   * signal so ranked-list usage is measurable alongside the tract detail.
   */
  function openRankedList(source: 'desktop_cta' | 'mobile_cta') {
    setPanelOpen(false)
    setRankOpen(true)
    track('metrics_panel_opened', { context: 'ranked_list', source })
  }

  /**
   * T-222/BEAD-0040: a ranked-list row click drives the SAME selection path a map click uses
   * (setActiveTract + close detail + clear geocode confidence + tract_selected). The panel has
   * already eased the camera to the tract centroid; here we only sync app state. The
   * tract_selected payload is identical to a map click's (geoid + coarse county/state) — the
   * ranked-list source is not distinguished in analytics (payload shape is owned by the
   * analytics workstream, docs/EVENTS.md).
   */
  function handleRankedSelect(t: CensusTract, centroid?: [number, number]) {
    setActiveTract(t)
    setPanelOpen(false)
    setGeocodeConfidence(null)
    setSearchedAddress(null) // T-258: a ranked-list pick has no address — export falls back to GEOID
    setAddressApproximate(false)
    reverseGeoReqIdRef.current++ // T-478: invalidate any in-flight click reverse-geocode
    // T-222 r2 (pick-3 UX guard): a ranked-list selection is a VISUAL tract pick, not a site
    // scoring. Clear any prior site-score state so the panel headlines the newly selected
    // tract's Opportunity Score (tract.lviScore) instead of a stale site score that described
    // the previous pin position — the single pin has just moved to this tract's centroid, which
    // that old score does not describe. (The map-click path doesn't need this: it refetches the
    // site score for the clicked point via handlePinMoved.)
    setSiteScoreResult(null)
    setSiteScoreError(null)
    setSiteScoreLoading(false)
    // T-222 r2: MOVE the single site pin to the tract centroid (from the feature geometry the
    // list already holds — no network) so the user sees exactly where the selected tract is.
    // VISUAL locator only: it does not re-run the pin-drop scoring flow (handlePinMoved) and the
    // tract_selected payload is unchanged. Pick-3 decision (ratified): map tract-clicks stay
    // exactly as they are (click-point pin + site scoring + pin_dropped, untouched); the centroid
    // move is LIST-click only, and because there is one pin slot it relocates that same pin.
    // T-628: activeLocation rides WITH the pin. It is what the block/block-group readout, the
    // block map layer and the EV/POI layers are all keyed on, so leaving it behind left the
    // site-specific readout describing wherever the user was looking BEFORE this pick — silently,
    // because a stale block still renders a plausible number. Same defect T-115 fixed for the
    // pin-drop path; this is the list-click path. No centroid on the row → the pin does not move,
    // so there is nothing to follow.
    if (centroid) {
      setPinLocation({ lat: centroid[1], lng: centroid[0] })
      setActiveLocation({ lat: centroid[1], lng: centroid[0] })
    }
    const geo = coarseGeoFromGeoid(t.tractId, fipsToAbbrev)
    track('tract_selected', { geoid: t.tractId, county: geo.county, state: geo.state })
    // T-233: the optimistic `t` above is built from the ranked row's raw map-source feature, which
    // can be an incomplete snapshot (headline present, every metric dashed) when the tracts source
    // is mid-reload. Re-hydrate authoritatively by geoid — the same server truth the map-click and
    // search paths use — and apply the full tract only if this geoid is still selected when it
    // resolves; on failure keep the optimistic tract. Tract-detail fetch only: no site-score
    // re-run, so the T-222 "no re-score on list-select" rule is untouched.
    void hydrateSelectedTract(t.tractId, {
      fetchTract: fetchTractByGeoid,
      isStillSelected: (geoid) => activeTractRef.current?.tractId === geoid,
      apply: setActiveTract,
    })
    // T-237: a ranked-list pick is a discrete selection → push a Back-able history entry. The pin
    // rode to the tract centroid (if the row carried one), else it stays where it was.
    urlState.commitSelection({
      tract: t.tractId,
      pin: centroid ? { lat: centroid[1], lng: centroid[0] } : pinLocation,
    })
  }

  // T-222/BEAD-0040 R2: the ranked list is reachable via a CTA at the tract tier (the entry
  // point to picking a promising tract), on both desktop and mobile.
  const rankAvailable = showTracts && tier === 'tract'

  /**
   * Sign out via AuthContext (finding a). AuthContext.signOut invalidates the Cognito
   * session AND clears the in-memory user, so the app re-renders as signed-out. Calling
   * authService.signOut directly would leave AuthContext.user populated (app still authed).
   * A rejected global sign-out (e.g. network) is caught so the click never throws unhandled.
   */
  function handleSignOut() {
    void signOut().catch(() => {
      /* globalSignOut failed (offline/expired); AuthContext still clears local state. */
    })
  }

  return (
    <div className="flex flex-col h-screen">
      <TopBar
        userEmail={userEmail}
        onSignOut={handleSignOut}
        onOpenSettings={() => setSettingsOpen(true)}
        isAdmin={user?.isAdmin ?? false}
        onOpenAdmin={onOpenAdmin}
        // T-500: the header Share control copies an authed deep-link to the current location.
        getShareUrl={urlState.getShareUrl}
      />
      <SettingsDialog open={settingsOpen} onOpenChange={setSettingsOpen} />
      <LeaveAppDialog open={showLeaveDialog} onStay={stay} onLeave={leave} />

      {/* T-526 (mobile map panning): the map region opts `pointer-events` back in. Opening the
          mobile bottom sheet makes vaul/Radix set `pointer-events: none` on <body>; T-495 opted
          the individual map OVERLAYS back in but not the map region, so the MapLibre canvas
          underneath stopped receiving touch the moment a location was scored — one-finger drags
          never reached dragPan (only stray taps landed, which read as "tap-to-score hijacks the
          pan"), and a pin behind the legend could not be panned back into view (Noreen,
          2026-07-27). Re-enabling it here means the canvas is always hit-testable — one-finger
          panning survives the open sheet. No effect on desktop (no drawer → <body> is never
          locked) and no effect on true modal dialogs, whose own full-screen overlay still
          intercepts pointer events above this region. */}
      <div className="relative flex-1 overflow-hidden pointer-events-auto">
        <MapView
          initialBounds={initialBounds}
          initialViewport={initialViewport}
          commandedViewport={commandedViewport}
          onViewportChange={urlState.syncViewport}
          onBboxChange={setBbox}
          onZoomChange={(z) => {
            setZoom(z)
            setTier((prev) => tierForZoom(z, prev)) // updater gives prev tier → hysteresis
          }}
        >
          {/* Null-rendering layer components */}
          {/* T-098/T-102: state tier — visible at national zoom-out (z<5), the tier the CONUS
              default camera (z4) opens on. Served from the CloudFront PMTiles pyramid via a signed
              URL when tile-auth is available, else the bbox-GeoJSON fallback. Gated by Score-Tracts. */}
          {usePmtiles ? (
            <PmtilesVectorLayer
              url={tileUrls.state}
              sourceLayer="state"
              idPrefix="state-pm"
              labelProp="state_abbrev"
              formatLabel={stateLabel}
              visible={showTracts && tier === 'state'}
              onSourceError={() => setPmtilesError(true)}
            />
          ) : (
            <StateLayer bbox={bbox} visible={showTracts && tier === 'state'} />
          )}
          {/* T-010/T-102: county tier — visible at regional zoom (5≤z<9). PMTiles signed-URL
              transport when available, else the bbox-GeoJSON fallback. Gated by the toggle. */}
          {usePmtiles ? (
            <PmtilesVectorLayer
              url={tileUrls.county}
              sourceLayer="county"
              idPrefix="county-pm"
              labelProp="county_name"
              visible={showTracts && tier === 'county'}
              onSourceError={() => setPmtilesError(true)}
            />
          ) : (
            <CountyLayer bbox={bbox} visible={showTracts && tier === 'county'} />
          )}
          <TractLayer
            bbox={bbox}
            visible={showTracts && tier === 'tract'}
            detail={detailForZoom(zoom)}
            onTruncatedChange={(truncated, shown) => setTruncatedShown(truncated ? shown : null)}
            tierFilter={tractTierFilter}
            typeFilter={tractTypeFilter}
            onTractClick={(t) => {
              setActiveTract(t)
              setPanelOpen(false)
              setGeocodeConfidence(null)
              const geo = coarseGeoFromGeoid(t.tractId, fipsToAbbrev)
              track('tract_selected', { geoid: t.tractId, county: geo.county, state: geo.state })
            }}
          />
          {/* T-061: NHS Interstate + US Route overlay. Self-gates to zoom >= ROADS_MIN_ZOOM
              (no fetch/render at national zoom — the choropleth is the story there). */}
          <RoadsLayer bbox={bbox} zoom={zoom} visible={showRoads} />
          {/* T-504: block + block-group detail around the SEARCHED SITE (T-502 ruling a —
              site-centric, no tiles). Fetches once per site, not per viewport move, and sits
              off the site-score critical path so the search itself cannot get slower. */}
          <SiteVicinityLayer site={activeLocation} visible={showVicinity} />
          {/* Always mounted; the toggle flips layer visibility (no source teardown/refetch). */}
          <EVChargerLayer
            location={activeLocation}
            visible={showEVChargers}
            onStationSelect={(loc) => handlePinMoved(loc, 'click')}
          />
          {/* T-135: drive-time isochrone around the active pin. Renders nothing until the
              user picks a duration (isochroneMinutes) AND a pin/location exists. */}
          <IsochroneLayer location={activeLocation} minutes={isochroneMinutes} />
          {/* Always mounted; visibility + category filter applied without teardown. */}
          <POILayerMgr
            location={activeLocation}
            visible={showPOI}
            enabledCategories={enabledPOICategories}
          />

          {/* Drop-a-pin null-renderer */}
          <PinMarker location={pinLocation} onPinMoved={handlePinMoved} />

          {/* T-229: subtle non-blocking "tiles loading" pill — driven by map lifecycle events
              (dataloading/idle), so a blank-while-loading view reads as "loading" not "broken". */}
          <TileLoadingIndicator />

          {/* T-275/F3: capped-viewport notice. The API returns only WHETHER the viewport
              was capped, never how many tracts matched — so this says "the first N", never
              "N of M". Same pill convention as TileLoadingIndicator; bottom-centre to clear
              the top-centre indicators. Non-blocking: the map stays fully usable. */}
          {truncatedShown !== null && (
            <div className="pointer-events-none absolute bottom-8 left-1/2 -translate-x-1/2 z-20">
              <div
                role="status"
                className="flex items-center gap-2 rounded-full bg-card/95 px-3 py-1.5 shadow-md text-xs font-medium text-foreground"
              >
                <span aria-hidden="true">⚠</span>
                Showing the first {truncatedShown.toLocaleString()} tracts — zoom in for complete
                coverage
              </div>
            </div>
          )}

          {/* Address search — top-left overlay.

              pointer-events-auto (T-495): while the mobile sheet is open, vaul/Radix puts
              `pointer-events: none` on <body>, and only the sheet itself opts back in. Everything
              layered over the map inherits it, so on a phone the search box went dead the moment a
              search returned a result — you could not search a second address without dismissing
              the sheet. Measured, not inferred: elementFromPoint over the field returned <html>.
              Each interactive map overlay opts itself back in; the map canvas keeps vaul's
              intended behaviour. */}
          <div className="pointer-events-auto absolute top-4 left-4 z-10 w-80">
            <MapSearchBar
              onSearchResult={handleSearchResult}
              pinPlaced={pinLocation !== null}
              onSearchActiveChange={setSearchActive}
            />
          </div>

          {/* Empty state — shown before any location is selected (SC-05). Suppressed once
              there is score feedback (e.g. an out-of-coverage result with no tract) so the
              EmptyState and the no-coverage panel don't show at once (T-081 F0).
              T-429: also suppressed while the address field is in use. This card sits at
              top-20 centre and the suggestion dropdown opens under the field at top-4 — on a
              phone, where the field spans the width, the card lands squarely on the list and
              hides the suggestions you are typing towards (Noreen, 2026-07-22). Clearing it on
              FOCUS (not on the first keystroke) means the list is unobstructed from the first
              suggestion. It also costs nothing: the card only says "search an address or drop
              a pin", which is precisely what you are already doing. */}
          {!activeTract && !hasScoreFeedback && !searchActive && (
            <div className="absolute top-20 left-1/2 -translate-x-1/2 z-10 w-72">
              <EmptyState />
            </div>
          )}

          {/* T-072 item 1: pin-drop → tract-resolve feedback (previously silent) */}
          {resolvingTract && (
            <div className="absolute top-20 left-1/2 -translate-x-1/2 z-20">
              <div className="flex items-center gap-2 rounded-full bg-card/95 px-3 py-1.5 shadow-md text-xs font-medium text-foreground">
                <Spinner size={14} label="Locating tract" />
                Locating tract…
              </div>
            </div>
          )}

          {/* Bottom-left stack — score legend + EV legend (when on) + layer controls
              (SC-04: relocated from top-right). flex-col so legends sit above the panel.

              T-495: at <md this stack measured 192×480 on a 390×844 phone — over half the map,
              and it sat on top of the pin a search had just dropped (measured: pin at x182-209,
              y412-453; stack at x16-208, y348-828). It now collapses behind one control, closed
              by default, the same idiom T-339 used for the POI category list. Desktop keeps the
              stack open outright — it has the room, and nothing there overlaps the map centre. */}
          <div
            className="pointer-events-auto absolute bottom-4 left-4 z-10 flex max-h-[calc(100%-2rem)] flex-col items-start gap-2"
            // T-495: the sheet is fixed to the viewport and covers the bottom SHEET_PEEK of it,
            // which is exactly where this corner sits — the control would be buried the moment a
            // search returned. Lift it clear while the sheet is showing.
            style={
              !isMd && sheetShowing ? { bottom: `calc(${SHEET_PEEK * 100}vh + 1rem)` } : undefined
            }
          >
            {(isMd || chromeOpen) && (
              <div className="oy-scroll flex min-h-0 flex-col gap-2 overflow-y-auto">
                {showTracts && <ScoreLegend />}
                {showEVChargers && <EVLegend />}
                {showRoads && <RoadsLegend />}
                {/* T-135: drive-time control — shown once a pin/location exists (the isochrone
                is anchored to it). Default Off; picking a duration renders the isochrone. */}
                {activeLocation && (
                  <IsochroneControl minutes={isochroneMinutes} onChange={setIsochroneMinutes} />
                )}
                <LayersControl
                  showTracts={showTracts}
                  showEVChargers={showEVChargers}
                  showPOI={showPOI}
                  showRoads={showRoads}
                  showVicinity={showVicinity}
                  onToggleTracts={() => {
                    const nv = !showTracts
                    setShowTracts(nv)
                    track('layer_toggled', { layer_id: 'tracts', enabled: nv })
                  }}
                  onToggleEV={() => {
                    const nv = !showEVChargers
                    setShowEVChargers(nv)
                    track('layer_toggled', { layer_id: 'ev', enabled: nv })
                  }}
                  onTogglePOI={() => {
                    const nv = !showPOI
                    setShowPOI(nv)
                    // T-222 r2 (P2): a manual toggle-off is permanent for the session — record it
                    // so the one-time auto-enable never re-fires against the user's choice.
                    if (!nv) {
                      poiManualOffRef.current = true
                      try {
                        sessionStorage.setItem(POI_MANUAL_OFF_KEY, '1')
                      } catch {
                        /* storage unavailable — ref still blocks auto re-enable this lifetime */
                      }
                    }
                    track('layer_toggled', { layer_id: 'poi', enabled: nv })
                  }}
                  onToggleRoads={() => {
                    const nv = !showRoads
                    setShowRoads(nv)
                    track('layer_toggled', { layer_id: 'roads', enabled: nv })
                  }}
                  onToggleVicinity={() => {
                    const nv = !showVicinity
                    setShowVicinity(nv)
                    track('layer_toggled', { layer_id: 'vicinity', enabled: nv })
                  }}
                  poiDisabled={zoom < POI_MIN_ZOOM}
                  // T-426: the tier/type filter is applied via map.setFilter on the TRACT layers,
                  // which only exist at the tract tier — at county/state zoom there is nothing for
                  // it to filter. Drive the control off the SAME tier value that decides whether
                  // TractLayer is mounted, so the hint can never drift from the actual condition.
                  tractFilterDisabled={tier !== 'tract'}
                  enabledPOICategories={enabledPOICategories}
                  onTogglePOICategory={(cat) => {
                    const next = enabledPOICategories.includes(cat)
                      ? enabledPOICategories.filter((c) => c !== cat)
                      : [...enabledPOICategories, cat]
                    setEnabledPOICategories(next)
                    track('filter_applied', {
                      filter_name: 'poi_category',
                      value_count: next.length,
                    })
                  }}
                  onSelectAllPOICategories={() => setEnabledPOICategories(POI_CATEGORY_NAMES)}
                  onClearPOICategories={() => setEnabledPOICategories([])}
                  tractTierFilter={tractTierFilter}
                  tractTypeFilter={tractTypeFilter}
                  onTractTierToggle={(tierId) => {
                    const next = tractTierFilter.includes(tierId)
                      ? tractTierFilter.filter((t) => t !== tierId)
                      : [...tractTierFilter, tierId]
                    setTractTierFilter(next)
                    track('filter_applied', { filter_name: 'tract_tier', value_count: next.length })
                  }}
                  onTractTypeToggle={(type) => {
                    const next = tractTypeFilter.includes(type)
                      ? tractTypeFilter.filter((t) => t !== type)
                      : [...tractTypeFilter, type]
                    setTractTypeFilter(next)
                    track('filter_applied', { filter_name: 'tract_type', value_count: next.length })
                  }}
                />
              </div>
            )}
            {/* T-495: the one control that stands in for the whole stack on a phone. It sits
                last so it stays pinned to the bottom-left corner the stack used to occupy. */}
            {!isMd && (
              <button
                type="button"
                onClick={() => setChromeOpen((open) => !open)}
                aria-expanded={chromeOpen}
                className="flex items-center gap-2 rounded-xl bg-card px-3 py-2 text-sm font-bold text-foreground
                           shadow-md border border-border hover:bg-accent/60 transition-colors
                           focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              >
                <Layers className="h-4 w-4 text-primary" aria-hidden="true" />
                {'Legend & layers'}
                <ChevronUp
                  className={cn('h-3.5 w-3.5 transition-transform', chromeOpen && 'rotate-180')}
                  aria-hidden="true"
                />
              </button>
            )}
          </div>

          {/* Mobile CTA (<md) — T-222 R2. Same anchor as the desktop CTA (top-right, at tract
              zoom); tapping opens the ranked list in the bottom drawer. Hidden while the ranked
              drawer is already open. */}
          {!isMd && rankAvailable && !rankOpen && (
            // pointer-events-auto: see the address-search overlay above (T-495).
            <div className="pointer-events-auto absolute top-16 right-4 z-10">
              <TopTractsCta onClick={() => openRankedList('mobile_cta')} />
            </div>
          )}

          {/* Desktop right rail (≥md) — hidden when either right-edge slide-out is open. Holds
              the T-222 R2 "Top tracts in view" CTA (at the tract tier — the entry point to the
              ranked list, which now slides out of the side panel like the tract detail) above
              the compact ScorePanel. ScorePanel shows only with an active tract or score
              feedback (T-081 F0). */}
          {isMd && !panelOpen && !rankOpen && (
            <div className="oy-scroll absolute top-24 right-4 z-10 flex w-80 flex-col gap-3 max-h-[calc(100%-7rem)] overflow-y-auto">
              {rankAvailable && <TopTractsCta onClick={() => openRankedList('desktop_cta')} />}
              {(activeTract || hasScoreFeedback) && (
                <ScorePanel
                  tract={activeTract}
                  siteScore={siteScoreResult}
                  siteScoreLoading={siteScoreLoading}
                  siteScoreError={siteScoreError}
                  confidence={geocodeConfidence}
                  onOpenFullDetails={() => openFullDetails('desktop_panel')}
                  exportAddress={searchedAddress}
                  addressApproximate={addressApproximate}
                  exportStateLabel={exportStateLabel}
                  exportPin={pinLocation}
                  siteClassification={siteClassification}
                />
              )}
            </div>
          )}

          {/* Desktop ranked-list slide-out (≥md) — T-222 R2. Opens from the CTA and slides out
              of the right edge using the SAME idiom/anchor as InfoSidePanel. Rendered INSIDE
              MapView so useMap()/querySourceFeatures resolve against the map's tract source
              (it reads the features the map already holds). A row click closes it and drives the
              shared selection path, revealing the tract detail (replace-in-place). */}
          {isMd && (
            <TopTractsSidePanel
              open={rankOpen}
              onClose={() => setRankOpen(false)}
              tier={tier}
              visible={showTracts}
              tierFilter={tractTierFilter}
              typeFilter={tractTypeFilter}
              onSelectTract={(t, centroid) => {
                setRankOpen(false)
                handleRankedSelect(t, centroid)
              }}
            />
          )}

          {/* Mobile bottom Drawer (<md) — modal=false keeps map interactive at peek (SC-02).
              Inside MapView for the same map-access reason (vaul portals the DOM, but React
              context still flows through the portal). T-222 R2: ONE surface at a time — the CTA
              opens the ranked list (rank mode); tapping a row collapses it to the tract detail
              (detail mode), replace-in-place, matching the desktop slide-out. */}
          {!isMd && (
            <Drawer
              modal={false}
              snapPoints={[SHEET_PEEK, SHEET_FULL]}
              activeSnapPoint={panelOpen || rankOpen ? SHEET_FULL : SHEET_PEEK}
              setActiveSnapPoint={(snap) => {
                const full = snap === SHEET_FULL
                // A swipe-down on the ranked list dismisses it; otherwise it drives the detail
                // peek↔full state exactly as before.
                if (rankOpen) setRankOpen(full)
                else setPanelOpen(full)
              }}
              // Open whenever there is a surface to show: the ranked list (rankOpen), an active
              // tract, or score feedback (T-081 F0, e.g. an out-of-coverage result with no tract).
              open={sheetShowing}
              onOpenChange={(open) => {
                if (!open) {
                  setRankOpen(false)
                  setActiveTract(null)
                }
              }}
            >
              <DrawerContent className="flex flex-col h-full bg-card">
                {rankOpen ? (
                  <>
                    {/* Ranked-list header — mirrors the desktop slide-out header (title + close). */}
                    <div className="h-12 flex-shrink-0 flex items-center justify-between px-4 border-b border-border border-t-2 border-t-primary">
                      <span className="text-sm font-bold text-foreground">Top tracts in view</span>
                      <button
                        onClick={() => setRankOpen(false)}
                        aria-label="Close top tracts panel"
                        className="ml-2 flex-shrink-0 rounded-md p-1 text-muted-foreground hover:text-foreground
                                   hover:bg-muted/50 focus-visible:outline-none focus-visible:ring-2
                                   focus-visible:ring-ring min-h-[36px] min-w-[36px] flex items-center justify-center"
                      >
                        <X className="h-4 w-4" />
                      </button>
                    </div>
                    <div className="oy-scroll overflow-y-auto flex-1">
                      <TopTractsList
                        tier={tier}
                        visible={showTracts}
                        tierFilter={tractTierFilter}
                        typeFilter={tractTypeFilter}
                        onSelectTract={(t, centroid) => {
                          setRankOpen(false)
                          handleRankedSelect(t, centroid)
                        }}
                      />
                    </div>
                  </>
                ) : mobileDetail && activeTract && !siteScoreError ? (
                  <>
                    {/* T-495: the full metrics, in the sheet. Same ScoreDrillDown the desktop
                        InfoSidePanel renders, headed the same way (title + one control) —
                        replace-in-place, so the sheet never stacks two surfaces. */}
                    <div className="h-12 flex-shrink-0 flex items-center justify-between px-4 border-b border-border border-t-2 border-t-primary">
                      <button
                        onClick={() => setMobileDetail(false)}
                        aria-label="Back to summary"
                        className="-ml-1 flex items-center gap-1 rounded-md px-1 text-xs text-muted-foreground
                                   hover:text-foreground hover:bg-muted/50 focus-visible:outline-none
                                   focus-visible:ring-2 focus-visible:ring-ring min-h-[36px]"
                      >
                        <ChevronLeft className="h-4 w-4" aria-hidden="true" />
                        Summary
                      </button>
                      <span className="ml-2 truncate text-xs text-muted-foreground">
                        Tract {activeTract.tractId}
                      </span>
                    </div>
                    <div className="oy-scroll flex-1 overflow-y-auto">
                      <ScoreDrillDown
                        tract={activeTract}
                        siteScore={siteScoreResult}
                        site={activeLocation}
                        siteClassification={siteClassification}
                      />
                    </div>
                  </>
                ) : (
                  <div className="oy-scroll overflow-y-auto flex-1 px-4 pb-4 space-y-3">
                    {(activeTract || hasScoreFeedback) && (
                      <ScorePanel
                        tract={activeTract}
                        siteScore={siteScoreResult}
                        siteScoreLoading={siteScoreLoading}
                        siteScoreError={siteScoreError}
                        confidence={geocodeConfidence}
                        onOpenFullDetails={() => openFullDetails('mobile_drawer')}
                        exportAddress={searchedAddress}
                        addressApproximate={addressApproximate}
                        exportStateLabel={exportStateLabel}
                        exportPin={pinLocation}
                        siteClassification={siteClassification}
                      />
                    )}
                  </div>
                )}
              </DrawerContent>
            </Drawer>
          )}

          {/* T-501: AI Strategy Advisor. Mounted INSIDE MapView so its CONTEXT assembly can read
              the map's ranked in-view tracts (useTopTracts); the panel itself is a fixed slide-over. */}
          <AdvisorMount
            open={advisorOpen}
            onClose={() => setAdvisorOpen(false)}
            activeTract={activeTract}
            siteScore={siteScoreResult}
            activeLocation={activeLocation}
            searchedAddress={searchedAddress}
            geocodeConfidence={geocodeConfidence}
            tier={tier}
            showTracts={showTracts}
          />
        </MapView>

        {/* Desktop InfoSidePanel (≥md) — fixed right edge, slides over map (T-007) */}
        {isMd && (
          <InfoSidePanel
            open={panelOpen}
            onClose={() => setPanelOpen(false)}
            tract={activeTract}
            siteScore={siteScoreResult}
            siteScoreLoading={siteScoreLoading}
            siteScoreError={siteScoreError}
            site={activeLocation}
          />
        )}

        {/* T-501: floating launcher (design decision) — hidden while the advisor is open. */}
        <AdvisorLauncher onOpen={() => setAdvisorOpen(true)} hidden={advisorOpen} />
      </div>
    </div>
  )
}
